import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'chatSlideOutWidth';
const MIN_RATIO = 1 / 3;
const MAX_RATIO = 1 / 2;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getInitialWidth = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseFloat(stored);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  } catch (_) {}
  return Math.round(window.innerWidth * MIN_RATIO);
};

export const useChatSlideOutResize = () => {
  const [width, setWidth] = useState(getInitialWidth);
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const getMinWidth = useCallback(() => Math.round(window.innerWidth * MIN_RATIO), []);
  const getMaxWidth = useCallback(() => Math.round(window.innerWidth * MAX_RATIO), []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const delta = startXRef.current - e.clientX;
      const newWidth = clamp(startWidthRef.current + delta, getMinWidth(), getMaxWidth());
      // Update DOM directly during drag — no setState, no re-renders.
      // This keeps the resize smooth (no React tree updates on every mousemove).
      if (containerRef.current) {
        containerRef.current.style.width = `${newWidth}px`;
      }
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const minW = getMinWidth();
      const maxW = getMaxWidth();
      const currentWidth = containerRef.current
        ? parseFloat(containerRef.current.style.width, 10) || startWidthRef.current
        : startWidthRef.current;
      const finalWidth = clamp(currentWidth, minW, maxW);
      if (containerRef.current) {
        containerRef.current.style.width = '';
      }
      setWidth(finalWidth);
      try {
        localStorage.setItem(STORAGE_KEY, String(finalWidth));
      } catch (_) {}
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [getMinWidth, getMaxWidth]);

  // Clamp width on window resize to keep within min/max ratios
  useEffect(() => {
    const handleWindowResize = () => {
      setWidth((current) => clamp(current, getMinWidth(), getMaxWidth()));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [getMinWidth, getMaxWidth]);

  return { width, handleMouseDown, containerRef };
};
